import { Badge, Divider, Drawer, List, ListItem, ListItemButton, ListItemText, Toolbar, Tooltip, Typography, useMediaQuery, ListItemIcon } from '@mui/material';
import styled from '@emotion/styled';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import PinIcon from '@mui/icons-material/PushPin';
import AssignmentIcon from '@mui/icons-material/AssignmentInd';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toggleNavDrawer, setAdminMode } from '../redux/slices/ui';
import { Assessment, Inventory, Receipt, Description, RequestQuote as RequestQuoteIcon, Security as SecurityIcon, Close as CloseIcon, VpnKey as VpnKeyIcon, Key as KeyIcon, ToggleOn as ToggleOnIcon, Event as EventIcon, EventBusy as EventBusyIcon, MoveToInbox as MoveToInboxIcon, AutoAwesome as AutoAwesomeIcon, AssignmentLate as AssignmentLateIcon, AccessTime as AccessTimeIcon, QuestionAnswer as QuestionAnswerIcon, LocalOffer as LocalOfferIcon, Extension as ExtensionIcon, MenuBook as MenuBookIcon, FolderShared as FolderSharedIcon } from '@mui/icons-material';
import { useHasPermission } from '../utils/permissionUtils';
import { useBrandName } from '../utils/brandUtils';
import { getPendingSuggestionCount } from '../redux/slices/suggestion';
import { useSocketEvent } from '../hooks/useSocketEvent';
import InstallAppButton from './InstallAppButton';
import { getUnassignedSummary } from '../redux/ducks/project';
import { useEnabledPlugins, useInternalPlugins } from '../clientPlugins';

const omit = (props) => (prop) => !props.includes(prop);

const drawerWidth = 200;
const railSpacing = 7;
const railSpacingSm = 8;

const widthTransition = (theme, collapsed) =>
    theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: collapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen,
    });

const DrawerStyled = styled(Drawer, { shouldForwardProp: omit(['collapsed']) })(({ theme, collapsed }) => ({
    width: collapsed ? theme.spacing(railSpacing) : drawerWidth,
    flexShrink: 0,
    transition: widthTransition(theme, collapsed),
    '& .MuiDrawer-paper': {
        width: collapsed ? theme.spacing(railSpacing) : drawerWidth,
        transition: widthTransition(theme, collapsed),
        ...(collapsed && {
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            '& .MuiListItemButton-root': {
                justifyContent: 'center',
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
            },
            '& .MuiListItemIcon-root': {
                minWidth: 0,
                justifyContent: 'center',
            },
            '& .MuiListItemText-root': {
                display: 'none',
            },
        }),
    },
    [theme.breakpoints.up('sm')]: {
        width: collapsed ? theme.spacing(railSpacingSm) : drawerWidth,
        '& .MuiDrawer-paper': {
            width: collapsed ? theme.spacing(railSpacingSm) : drawerWidth,
        },
    },
}));

const DrawerContainer = styled('div')({
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
});

const DrawerFooter = styled(List)({
    flexShrink: 0,
});

const ExitAdminButton = styled(ListItemButton)(({ theme }) => ({
    backgroundColor: theme.palette.warning.light,
    '&:hover': {
        backgroundColor: theme.palette.warning.main,
    },
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const TitleStyled = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
}));

const NavBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        fontSize: theme.typography.size.xxs,
        height: 16,
        minWidth: 16,
    },
}));

export const NavTooltip = ({ collapsed, label, children }) =>
    collapsed ? (
        <Tooltip title={label} placement="right">
            {children}
        </Tooltip>
    ) : (
        children
    );

const NavDrawer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const brandName = useBrandName();
    const drawerOpen = useSelector((state) => state.ui.navDrawer);
    const navCollapsed = useSelector((state) => state.ui.navCollapsed);
    const adminMode = useSelector((state) => state.ui.adminMode);
    const canSeeAdmin = hasPermission("canSeeAdmin");
    const canSeeSuggestions = hasPermission("canSeeSuggestions");
    const calendarStatusLoaded = useSelector((state) => state.calendar?.status?.loaded === true);
    const calendarOptOut = useSelector((state) => state.calendar?.status?.optOut);
    const showMeetings = hasPermission("canSeeInternal") && calendarStatusLoaded && !calendarOptOut;
    const pendingSuggestions = useSelector((state) => state.suggestion.pendingCount);
    const suggestionsRefresh = useSelector((state) => state.refreshTriggers.suggestions);
    const projectRefresh = useSelector((state) => state.refreshTriggers.project);
    const taskRefresh = useSelector((state) => state.refreshTriggers.task);
    const canSeeManagement = hasPermission("canSeeManagement");
    const [untriagedCount, setUntriagedCount] = useState(0);
    const enabledPlugins = useEnabledPlugins();
    const internalPlugins = useInternalPlugins();
    const pluginNav = [
        ...enabledPlugins.flatMap((plugin) => plugin.nav.map((item) => ({ ...item, key: `${plugin.id}:${item.path}` }))),
        ...internalPlugins.flatMap((plugin) => plugin.internal.nav.map((item) => ({ ...item, key: `${plugin.id}:internal:${item.path}` }))),
    ];
    const isMountedRef = useRef(true);

    const collapsed = !isSmallScreen && navCollapsed;

    // Automatically set admin mode based on current route, but only for users with the admin permission
    useEffect(() => {
        const isAdminRoute = location.pathname.startsWith('/admin');
        dispatch(setAdminMode(isAdminRoute && canSeeAdmin));
    }, [location.pathname, dispatch, canSeeAdmin]);

    // Counts the same slice the page opens with (mine + pending), so the badge always matches the list.
    useEffect(() => {
        if (canSeeSuggestions) {
            dispatch(getPendingSuggestionCount({ scope: 'mine' }));
        }
    }, [dispatch, canSeeSuggestions, suggestionsRefresh]);

    const recount = useCallback(() => {
        if (canSeeSuggestions) dispatch(getPendingSuggestionCount({ scope: 'mine' }));
    }, [dispatch, canSeeSuggestions]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!canSeeManagement) return;
        dispatch(getUnassignedSummary((data) => {
            if (!isMountedRef.current) return;
            const brandNew = (data?.projects?.cells?.noDeptNoSkill || 0) + (data?.tasks?.cells?.noDeptNoSkill || 0);
            setUntriagedCount(brandNew);
        }));
    }, [dispatch, canSeeManagement, projectRefresh, taskRefresh]);

    useSocketEvent('suggestion:new', recount);
    useSocketEvent('suggestion:updated', recount);

    const handleToggleDrawer = () => {
        dispatch(toggleNavDrawer());
    };

    const handleNavigateToAdmin = () => {
        drawerNavigation('/admin');
    };

    const handleExitAdmin = () => {
        drawerNavigation('/');
    };

    const drawerVariant = isSmallScreen ? 'temporary' : 'permanent';

    const drawerNavigation = (pageURL) => {
        navigate(pageURL);
        if (drawerOpen) {
            handleToggleDrawer();
        }
    };

    const adminMenuItems = [
        { label: "Configuration", path: "/admin/config", icon: SettingsIcon },
        { label: "Departments", path: "/admin/departments", icon: BusinessIcon },
        { label: "Roles", path: "/admin/roles", icon: AssignmentIcon },
        { label: "Role Dashboards", path: "/admin/role-dashboards", icon: DashboardCustomizeIcon },
        { label: "Permissions", path: "/admin/permissions", icon: SecurityIcon },
        { label: "Skills", path: "/admin/skills", icon: AccountTreeIcon },
        { label: "Holidays", path: "/admin/holidays", icon: EventBusyIcon },
        { label: "Service Accounts", path: "/admin/service-accounts", icon: VpnKeyIcon },
        { label: "User API Keys", path: "/admin/api-keys", icon: KeyIcon, permission: "editUsers" },
        { label: "User Access", path: "/admin/user-access", icon: ToggleOnIcon },
        { label: "Plugins", path: "/admin/plugins", icon: ExtensionIcon },
    ].filter((item) => !item.permission || hasPermission(item.permission));

    const suggestionsHint = pendingSuggestions > 0
        ? `${pendingSuggestions} suggestion${pendingSuggestions === 1 ? '' : 's'} waiting for your review`
        : 'Actions the assistant recommends. Nothing is waiting for you right now.';

    return (
        <DrawerStyled variant={drawerVariant} open={drawerOpen} onClose={handleToggleDrawer} collapsed={collapsed}>
            <Toolbar>
                <TitleStyled variant="h6" onClick={() => drawerNavigation("/")}>
                    {brandName}
                </TitleStyled>
            </Toolbar>
            <DrawerContainer>
                {adminMode && canSeeAdmin ? (
                    <>
                        {/* Admin Mode Menu */}
                        <List>
                            <NavTooltip collapsed={collapsed} label="Exit Admin Mode">
                                <ExitAdminButton
                                    key="ExitAdmin"
                                    onClick={handleExitAdmin}
                                >
                                    <ListItemIcon>
                                        <CloseIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Exit Admin Mode" />
                                </ExitAdminButton>
                            </NavTooltip>
                            <SectionDivider />
                            {adminMenuItems.map((item) => (
                                <NavTooltip key={item.label} collapsed={collapsed} label={item.label}>
                                    <ListItemButton
                                        onClick={() => drawerNavigation(item.path)}
                                    >
                                        <ListItemIcon>
                                            <item.icon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                </NavTooltip>
                            ))}
                        </List>
                    </>
                ) : (
                    <>
                        {/* Normal User Menu */}
                        <List>
                            <NavTooltip collapsed={collapsed} label="Dashboard">
                                <ListItemButton key="Dashboard" onClick={() => drawerNavigation("/")}>
                                    <ListItemIcon>
                                        <DashboardIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Dashboard" />
                                </ListItemButton>
                            </NavTooltip>
                            {!hasPermission('canSeeInternal') && <NavTooltip collapsed={collapsed} label="Quotes">
                                <ListItemButton key="MyQuotes" onClick={() => drawerNavigation("/my-quotes")}>
                                    <ListItemIcon>
                                        <RequestQuoteIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Quotes" />
                                </ListItemButton>
                            </NavTooltip>}
                            {!hasPermission('canSeeInternal') && <NavTooltip collapsed={collapsed} label="Billing">
                                <ListItemButton key="MyBilling" onClick={() => drawerNavigation("/my-billing")}>
                                    <ListItemIcon>
                                        <Receipt color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Billing" />
                                </ListItemButton>
                            </NavTooltip>}
                            <NavTooltip collapsed={collapsed} label="Clients">
                                <ListItemButton key="Clients" onClick={() => drawerNavigation("/clients")}>
                                    <ListItemIcon>
                                        <BusinessIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Clients" />
                                </ListItemButton>
                            </NavTooltip>
                            {hasPermission('listClients') && hasPermission('canSeeInternal') && <NavTooltip collapsed={collapsed} label="Documents">
                                <ListItemButton key="Documents" onClick={() => drawerNavigation("/documents")}>
                                    <ListItemIcon>
                                        <FolderSharedIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Documents" />
                                </ListItemButton>
                            </NavTooltip>}
                            <NavTooltip collapsed={collapsed} label="Accounts">
                                <ListItemButton key="Accounts" onClick={() => drawerNavigation("/accounts")}>
                                    <ListItemIcon>
                                        <AccountTreeIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Accounts" />
                                </ListItemButton>
                            </NavTooltip>
                            {hasPermission("canSeeInternal") && (
                                <>
                                    <NavTooltip collapsed={collapsed} label="Pinned">
                                        <ListItemButton key="Pinned" onClick={() => drawerNavigation("/accounts/pinned")}>
                                            <ListItemIcon>
                                                <PinIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="Pinned" />
                                        </ListItemButton>
                                    </NavTooltip>
                                    <NavTooltip collapsed={collapsed} label="Assignments">
                                        <ListItemButton key="Assignments" onClick={() => drawerNavigation("/assignments")}>
                                            <ListItemIcon>
                                                <AssignmentIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="Assignments" />
                                        </ListItemButton>
                                    </NavTooltip>
                                    <NavTooltip collapsed={collapsed} label="My Hours">
                                        <ListItemButton key="MyHours" onClick={() => drawerNavigation("/my-hours")}>
                                            <ListItemIcon>
                                                <AccessTimeIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="My Hours" />
                                        </ListItemButton>
                                    </NavTooltip>
                                </>
                            )}
                            {canSeeManagement && (
                                <NavTooltip collapsed={collapsed} label="Unassigned Work">
                                    <ListItemButton key="UnassignedTriage" onClick={() => drawerNavigation("/unassigned-triage")}>
                                        <ListItemIcon>
                                            <NavBadge badgeContent={untriagedCount} color="warning" max={99}>
                                                <AssignmentLateIcon color="primary" />
                                            </NavBadge>
                                        </ListItemIcon>
                                        <ListItemText primary="Unassigned Work" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {showMeetings && (
                                <NavTooltip collapsed={collapsed} label="Meetings">
                                    <ListItemButton key="Meetings" onClick={() => drawerNavigation("/meetings")}>
                                        <ListItemIcon>
                                            <EventIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Meetings" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {canSeeSuggestions && (
                                <Tooltip
                                    title={collapsed ? `Suggestions — ${suggestionsHint}` : suggestionsHint}
                                    placement="right"
                                >
                                    <ListItemButton key="Suggestions" onClick={() => drawerNavigation("/ai-actions")}>
                                        <ListItemIcon>
                                            <NavBadge badgeContent={pendingSuggestions} color="warning" max={99}>
                                                <AutoAwesomeIcon color="primary" />
                                            </NavBadge>
                                        </ListItemIcon>
                                        <ListItemText primary="Suggestions" />
                                    </ListItemButton>
                                </Tooltip>
                            )}
                            {hasPermission("canManageInbox") && (
                                <NavTooltip collapsed={collapsed} label="Email Triage">
                                    <ListItemButton key="EmailTriage" onClick={() => drawerNavigation("/inbound-mail")}>
                                        <ListItemIcon>
                                            <MoveToInboxIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Email Triage" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {hasPermission("canManageQuotes") && (
                                <NavTooltip collapsed={collapsed} label="Quotes">
                                    <ListItemButton key="Quotes" onClick={() => drawerNavigation("/quotes")}>
                                        <ListItemIcon>
                                            <RequestQuoteIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Quotes" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {hasPermission("canSeeInternal") && (
                                <NavTooltip collapsed={collapsed} label="Knowledge Base">
                                    <ListItemButton key="KnowledgeBase" onClick={() => drawerNavigation("/kb")}>
                                        <ListItemIcon>
                                            <MenuBookIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Knowledge Base" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {hasPermission("viewTemplates") && (
                                <NavTooltip collapsed={collapsed} label="Templates">
                                    <ListItemButton key="Templates" onClick={() => drawerNavigation("/templates")}>
                                        <ListItemIcon>
                                            <Description color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Templates" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {hasPermission("tagManagement") && (
                                <NavTooltip collapsed={collapsed} label="Tags">
                                    <ListItemButton key="Tags" onClick={() => drawerNavigation("/tags")}>
                                        <ListItemIcon>
                                            <LocalOfferIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Tags" />
                                    </ListItemButton>
                                </NavTooltip>
                            )}
                            {hasPermission("canRunReports") && (
                                <>
                                    <NavTooltip collapsed={collapsed} label="Master Report">
                                        <ListItemButton key="MasterReport" onClick={() => drawerNavigation("/master-report")}>
                                            <ListItemIcon>
                                                <Inventory color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="Master Report" />
                                        </ListItemButton>
                                    </NavTooltip>
                                    <NavTooltip collapsed={collapsed} label="Runnit Reports">
                                        <ListItemButton key="RunnitReports" onClick={() => drawerNavigation("/runnit-reports")}>
                                            <ListItemIcon>
                                                <Assessment color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="Runnit Reports" />
                                        </ListItemButton>
                                    </NavTooltip>
                                </>
                            )}
                            {hasPermission("canRunBilling") && (
                                <>
                                    <NavTooltip collapsed={collapsed} label="Billing">
                                        <ListItemButton key="Billing" onClick={() => drawerNavigation("/billing")}>
                                            <ListItemIcon>
                                                <Receipt color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary="Billing" />
                                        </ListItemButton>
                                    </NavTooltip>
                                </>
                            )}
                            <InstallAppButton collapsed={collapsed} />
                        </List>
                        {pluginNav.length > 0 && (
                            <>
                                <SectionDivider />
                                <List>
                                    {pluginNav.map((item) => (
                                        <NavTooltip key={item.key} collapsed={collapsed} label={item.label}>
                                            <ListItemButton onClick={() => drawerNavigation(item.path)}>
                                                <ListItemIcon>
                                                    <item.icon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText primary={item.label} />
                                            </ListItemButton>
                                        </NavTooltip>
                                    ))}
                                </List>
                            </>
                        )}
                    </>
                )}
            </DrawerContainer>
            {canSeeAdmin && !adminMode && (
                <DrawerFooter>
                    <Divider />
                    <NavTooltip collapsed={collapsed} label="Admin">
                        <ListItemButton key="Admin" onClick={handleNavigateToAdmin}>
                            <ListItemIcon>
                                <SettingsIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="Admin" />
                        </ListItemButton>
                    </NavTooltip>
                </DrawerFooter>
            )}
        </DrawerStyled>
    );
};

export default NavDrawer;
